import { Calendar, Edit01, InfoCircle, MarkerPin01 } from '@untitled-ui/icons-react';
import { XClose } from '@untitled-ui/icons-react';
import classNames from 'classnames';
import React, { useContext } from 'react';

import { Button } from '@myhealth-raccoon/components';

import { ClinicBillingPolicy } from './ClinicBillingPolicy';
import { PricingDetails } from './PricingDetails';
import DoctorIcon from 'src/assets/doctor.svg?react';
import { CancellationPolicyNotice } from 'src/components/CancellationPolicyNotice.tsx';
import { availabilityPickerContext } from 'src/contexts/availabilityPicker';
import { cancellationPolicyContext } from 'src/contexts/cancellationPolicy';
import { clinicInfoContext } from 'src/contexts/clinicInfo';
import { useShouldHideCloseButton } from 'src/hooks/shouldHideCloseButton';
import { useAppointmentTypeSelector, useClinicSelector, useDoctorSelector } from 'src/hooks/useAsyncSelector';
import { useClinicTime } from 'src/hooks/useClinicTime.ts';
import { useExit } from 'src/hooks/useExit';
import { AppointmentServiceType } from 'src/services/booking';
import { useSelector } from 'src/store';
import {
    determineAppointmentServiceType,
    isAfterHours,
    isExpressTelehealth,
    isTelehealthNow,
} from 'src/utils/appointmentTypes';
import { clinicName, doctorAvatarSrc, doctorName } from 'src/utils/name';

export function AppointmentSidebar() {
    const { setOpen: setEditingAppointmentTime } = useContext(availabilityPickerContext);
    const exit = useExit();

    const { setOpen: setViewingClinicInfo } = useContext(clinicInfoContext);
    const { setOpen: setViewingCancellationPolicy } = useContext(cancellationPolicyContext);
    const hideCloseButton = useShouldHideCloseButton();

    const { dateTime, reservedBooking } = useSelector((state) => state.appointment);
    const { data: clinic, ...clinicRequest } = useClinicSelector();
    const { data: doctor, ...doctorRequest } = useDoctorSelector();
    const { data: appointmentType, ...appointmentTypeRequest } = useAppointmentTypeSelector();

    const appointmentTime = useClinicTime(dateTime, clinic?.ianaTimeZoneId);

    const renderAppointmentTypeContent = () => {
        if (!appointmentType) {
            return <></>;
        }

        const serviceType = determineAppointmentServiceType(appointmentType);

        let label = '';
        switch (serviceType) {
            case AppointmentServiceType.Telehealth:
                label = 'Telehealth';
                break;
            case AppointmentServiceType.FaceToFace:
                label = 'Face to Face';
                break;
            case AppointmentServiceType.ExpressTelehealth:
                label = 'Express Telehealth';
                break;
        }

        if (serviceType === AppointmentServiceType.Telehealth) {
            return label;
        }

        return (
            <>
                {label}
                <br className="hidden lg:block" />
                <span className="lg:hidden"> - </span>
                <span className="font-medium text-gray-600">{appointmentType.appointmentName}</span>
            </>
        );
    };

    return (
        <div className="flex flex-col gap-4 lg:gap-8">
            <div
                className={classNames('flex flex-row items-center', {
                    'animate-pulse': doctorRequest.isLoading,
                })}
            >
                <figure className="size-10 shrink-0 self-start overflow-hidden rounded-full border bg-gray-300 lg:size-14">
                    {doctor && (
                        <img
                            className="animate-fade-in size-full object-cover"
                            role="presentation"
                            src={doctorAvatarSrc(doctor)}
                        />
                    )}
                </figure>

                <div className="ml-4 flex-1 overflow-hidden">
                    <h3 className={classNames('text-base leading-none font-medium text-gray-700 lg:text-lg')}>
                        {doctorRequest.isLoading ? (
                            <span className="inline-block h-[0.5em] w-32 rounded-full bg-current opacity-25"></span>
                        ) : doctor ? (
                            doctorName(doctor)
                        ) : (
                            doctorRequest.error?.message
                        )}
                    </h3>
                    <div className="font-base text-sm text-gray-600 lg:text-base">
                        {doctorRequest.isLoading ? (
                            <span className="inline-block h-[0.5em] w-40 rounded-full bg-current opacity-25"></span>
                        ) : doctor?.umbracoRole ? (
                            <p className="hidden lg:block">{doctor.umbracoRole}</p>
                        ) : (
                            ''
                        )}
                        {appointmentType && !isAfterHours(appointmentType) && !isTelehealthNow(appointmentType) && (
                            <div className="mt-1 flex flex-row gap-2 overflow-hidden lg:hidden">
                                {clinicRequest.isLoading ? (
                                    <span className="inline-block h-[0.5em] w-40 rounded-full bg-current opacity-25"></span>
                                ) : (
                                    <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                                        {clinicName(clinic)}
                                    </span>
                                )}

                                <button
                                    className={classNames('text-sm font-semibold text-gray-600', {
                                        'pointer-events-none opacity-25': clinicRequest.isLoading || !clinic,
                                    })}
                                    disabled={clinicRequest.isLoading}
                                    onClick={() => setViewingClinicInfo(true)}
                                >
                                    <InfoCircle className="inline size-4" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                {!hideCloseButton && (
                    <XClose className="flex h-6 w-6 cursor-pointer lg:hidden" onClick={() => exit()} />
                )}
            </div>
            <div>
                <h3 className="mb-2 hidden text-lg font-semibold text-gray-900 lg:block">Appointment Details</h3>
                <ul className="flex flex-col gap-y-2 lg:gap-y-4">
                    <li className="relative flex w-full flex-row gap-2.5">
                        <DoctorIcon className="h-6 w-6 text-gray-400" />
                        <div>
                            {appointmentTypeRequest.isLoading ? (
                                <span className="inline-block h-[0.5em] w-40 rounded-full bg-current opacity-25"></span>
                            ) : appointmentType ? (
                                <>
                                    <p className="text-sm font-medium text-gray-700 lg:text-base">
                                        {renderAppointmentTypeContent()}
                                    </p>
                                </>
                            ) : (
                                appointmentTypeRequest?.error?.message
                            )}
                        </div>
                    </li>
                    {!isExpressTelehealth(appointmentType!) ? (
                        <li className="relative flex w-full flex-row items-center gap-2.5 lg:items-start">
                            <Calendar className="size-6 text-gray-400" />
                            <div className="flex-1 text-sm font-medium text-gray-700 lg:text-base">
                                <p className="hidden lg:block">
                                    {appointmentTime?.format('dddd, D MMMM YYYY')}
                                    <br />
                                    {appointmentTime?.format('LT')}
                                </p>
                                <p className="block lg:hidden">{appointmentTime?.format('ddd, D MMMM [at] LT')}</p>
                            </div>
                            <Button
                                variant="link"
                                className="-my-4 flex gap-2 lg:m-0"
                                disabled={!!reservedBooking}
                                onClick={() => setEditingAppointmentTime(true)}
                            >
                                <Edit01 className="size-5" />
                                Modify
                            </Button>
                        </li>
                    ) : (
                        <li className="relative flex w-full flex-row items-center gap-2.5 lg:items-start">
                            <Calendar className="size-6 text-gray-400" />
                            <div className="flex-1 text-sm font-medium text-gray-700 lg:text-base">
                                <p>{appointmentTime?.format('dddd, D MMMM YYYY')}</p>
                            </div>
                        </li>
                    )}
                    {appointmentType && !isAfterHours(appointmentType) && !isTelehealthNow(appointmentType) && (
                        <li
                            className={classNames('relative hidden w-full flex-row gap-2.5 lg:flex', {
                                'animate-pulse': clinicRequest.isLoading,
                            })}
                        >
                            <MarkerPin01 className="h-6 w-6 text-gray-400" />
                            <div>
                                <p className="text-base font-medium text-gray-700">
                                    {clinicRequest.isLoading ? (
                                        <span className="inline-block h-[0.5em] w-40 rounded-full bg-current opacity-25"></span>
                                    ) : clinic ? (
                                        clinicName(clinic)
                                    ) : (
                                        clinicRequest.error?.message
                                    )}
                                </p>
                                <button
                                    className={classNames('mt-1 text-sm font-semibold text-gray-600 underline', {
                                        'pointer-events-none opacity-25': clinicRequest.isLoading || !clinic,
                                    })}
                                    disabled={clinicRequest.isLoading}
                                    onClick={() => setViewingClinicInfo(true)}
                                >
                                    View info
                                </button>
                            </div>
                        </li>
                    )}
                    <li
                        className={classNames('lg:hidden', {
                            hidden: appointmentType && isExpressTelehealth(appointmentType),
                        })}
                    >
                        <button
                            className="text-sm font-semibold text-gray-600 underline"
                            onClick={() => setViewingCancellationPolicy(true)}
                        >
                            View Pricing & Cancellation Policy Info
                        </button>
                    </li>
                </ul>
            </div>
            <div className="hidden rounded-xl border border-gray-200 bg-white px-4 py-6 lg:block">
                {appointmentType && (isAfterHours(appointmentType) || isTelehealthNow(appointmentType)) ? (
                    <>
                        <h4 className="mb-1 text-base font-semibold text-gray-900">Pricing details</h4>
                        <PricingDetails appointmentType={appointmentType} />
                        <hr className="my-4" />
                        This is a private service. You will not receive any Medicare Rebates for this consultation.
                    </>
                ) : (
                    clinic && (
                        <>
                            {!isExpressTelehealth(appointmentType!) && (
                                <>
                                    <h4 className="mb-1 text-base font-semibold text-gray-900">Pricing details</h4>
                                    <ClinicBillingPolicy clinic={clinic} />
                                    <hr className="my-4" />
                                </>
                            )}
                            {clinic?.cancellationPolicyHtml && (
                                <>
                                    <h4 className="mb-1 text-base font-semibold text-gray-900">
                                        {clinic.cancellationTitle ? clinic.cancellationTitle : 'Cancellation policy'}
                                    </h4>
                                    <CancellationPolicyNotice
                                        html={clinic.cancellationPolicyHtml}
                                        appointmentType={appointmentType}
                                    />
                                </>
                            )}
                        </>
                    )
                )}
            </div>
        </div>
    );
}
